import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Calendar, Clock, CheckCircle2, Send } from "lucide-react";
import { api, apiError } from "../lib/api";
import { SERVICE_OPTIONS } from "../data/site";
import { Reveal, PageHeader } from "../components/Reveal";

const inputCls =
  "w-full py-3.5 px-4 bg-surface border border-border rounded-xl text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand transition-all";

function todayStr() {
  return new Date().toISOString().split("T")[0];
}

export default function Appointment() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", address: "", message: "" });
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!date) return setSlots([]);
    setSlot("");
    api.get(`/appointments/slots?date=${date}`).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Choisissez une date.");
    if (!slot) return toast.error("Choisissez un créneau horaire.");
    if (!form.service) return toast.error("Sélectionnez un service.");
    setLoading(true);
    try {
      await api.post("/appointments", { ...form, date, slot });
      setDone(true);
      toast.success("Rendez-vous enregistré !");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="appointment-page">
      <PageHeader overline="Rendez-vous" title="Prenez rendez-vous en ligne" subtitle="Choisissez le créneau qui vous convient. Nous vous confirmons votre rendez-vous par téléphone." />

      <section className="max-w-4xl mx-auto px-5 md:px-10 py-24">
        <Reveal>
          <div className="bg-white border border-border rounded-3xl p-8 md:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            {done ? (
              <div className="text-center py-12" data-testid="appointment-success">
                <div className="w-16 h-16 bg-brand/10 rounded-full flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-brand" /></div>
                <h2 className="font-display text-2xl font-extrabold text-navy mt-6">Rendez-vous enregistré !</h2>
                <p className="text-text-muted mt-3">Le {date} à {slot}. Nous vous confirmerons par téléphone.</p>
                <button onClick={() => { setDone(false); setDate(""); setSlot(""); setForm({ name: "", email: "", phone: "", service: "", address: "", message: "" }); }} className="mt-7 bg-navy text-white font-bold py-3 px-7 rounded-full hover:bg-navy-light transition-colors">
                  Nouveau rendez-vous
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="appointment-form">
                <div>
                  <span className="flex items-center gap-2 text-sm font-semibold text-navy mb-1.5"><Calendar className="w-4 h-4 text-brand" /> Date souhaitée *</span>
                  <input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="appointment-date" className={inputCls} required />
                </div>

                {date && (
                  <div>
                    <span className="flex items-center gap-2 text-sm font-semibold text-navy mb-2"><Clock className="w-4 h-4 text-brand" /> Créneau *</span>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {slots.map((s) => (
                        <button
                          type="button"
                          key={s.slot}
                          disabled={!s.available}
                          onClick={() => setSlot(s.slot)}
                          data-testid={`slot-${s.slot}`}
                          className={`py-2.5 rounded-xl text-sm font-bold border transition-all ${
                            slot === s.slot ? "bg-brand text-white border-brand"
                            : s.available ? "bg-surface text-navy border-border hover:border-brand"
                            : "bg-slate-100 text-slate-300 border-transparent cursor-not-allowed line-through"
                          }`}
                        >
                          {s.slot}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-5">
                  <input required value={form.name} onChange={set("name")} data-testid="appointment-name" className={inputCls} placeholder="Nom complet *" />
                  <input required value={form.phone} onChange={set("phone")} data-testid="appointment-phone" className={inputCls} placeholder="Téléphone *" />
                </div>
                <input required type="email" value={form.email} onChange={set("email")} data-testid="appointment-email" className={inputCls} placeholder="Email *" />
                <select required value={form.service} onChange={set("service")} data-testid="appointment-service" className={inputCls}>
                  <option value="">Service concerné *</option>
                  {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input value={form.address} onChange={set("address")} data-testid="appointment-address" className={inputCls} placeholder="Adresse d'intervention" />
                <textarea rows={4} value={form.message} onChange={set("message")} data-testid="appointment-message" className={`${inputCls} resize-none`} placeholder="Précisions (optionnel)" />

                <button type="submit" disabled={loading} data-testid="appointment-submit" className="w-full bg-brand hover:bg-brand-dark text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                  {loading ? "Envoi…" : <>Confirmer le rendez-vous <Send className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
